import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { fetchEmployee } from "../api/employee";
import {
  addUserAccessOverrideEvent,
  fetchLatestUserAccessOverrideEventsByStation,
  isUserAuthorizedByServer,
} from "../api/userAccess";
import ManageAccessDialog from "../components/ManageAccessDialog";
import PageLoading from "../components/PageLoading";
import UnauthorizedMessage from "../components/UnauthorizedMessage";
import UserAccessOverrideEventTable from "../components/UserAccessOverrideEventTable";
import { selectUserSession } from "../slices/userSession/userSessionSlice";
import {
  ADMIN_ACCESS,
  BREADCRUMBS_TITLE,
  EMPLOYEE_ACCESS,
  HOME_PATH,
  MULTI_STATION_ADMIN_ACCESS,
} from "../util/constants";
import { ADMIN_MOBILE_WARNING_MESSAGE } from "../util/messageConstants";
import { logout } from "../util/authentication";
import { getUserDataFromEmployeeService } from "../util/employeeDataRetriever";
import {
  validateAccessLevel,
  validateAddAdminStationForManageAccess,
  validateEmployeeDataForManageAccess,
  validateEmployeeId,
  validateStation,
  validateSubmitAdminStationsForManageAccess,
} from "../util/inputValidation";
import { formatEmployeeIdFromKronos } from "../util/miscFormatter";
import { convertUserAccessOverrideEventUTCIntegerFieldsToStationTimeZoneLuxonDateTime } from "../util/timeZoneService";
import { getJobTitleGivenJobCode, getWorkgroupGivenJobCode } from "../util/workgroupMapper";
import StarsBreadcrumbs from "../components/fundamentals/StarsBreadcrumbs";

const AccessExceptionsPage = (props) => {
  const userSession = useSelector(selectUserSession);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [stationLatestUserAccessOverrideEvents, setStationLatestUserAccessOverrideEvents] = useState([]);
  const [selectedSuperuserStation, setSelectedSuperuserStation] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Manage Access Dialog State
  const [dialogStationErrors, setDialogStationErrors] = useState("");
  const [dialogAccessOverrideEvent, setDialogAccessOverrideEvent] = useState({});
  const [dialogEmployeeId, setDialogEmployeeId] = useState("");
  const [dialogEmployeeSearchTriggered, setDialogEmployeeSearchTriggered] = useState(false);
  const [dialogEmployeeSearchLoaded, setDialogEmployeeSearchLoaded] = useState(false);
  const [dialogEmployeeIdErrors, setDialogEmployeeIdErrors] = useState("");
  const [dialogEmployeeData, setDialogEmployeeData] = useState(null);
  const [dialogEmployeeFirstName, setDialogEmployeeFirstName] = useState("");
  const [dialogEmployeeLastName, setDialogEmployeeLastName] = useState("");
  const [dialogEmployeeJobCode, setDialogEmployeeJobCode] = useState("");
  const [dialogEmployeeTempStation, setDialogEmployeeTempStation] = useState("");
  const [dialogEmployeeHomeStation, setDialogEmployeeHomeStation] = useState("");
  const [dialogEmployeeActiveStation, setDialogEmployeeActiveStation] = useState("");
  const [dialogEmployeeJobTitle, setDialogEmployeeJobTitle] = useState("");
  const [dialogEmployeeWorkgroup, setDialogEmployeeWorkgroup] = useState("");
  const [dialogSelectedAccessLevel, setDialogSelectedAccessLevel] = useState("");
  const [dialogSelectedAccessLevelErrors, setDialogSelectedAccessLevelErrors] = useState("");
  const [dialogCurrentStation, setDialogCurrentStation] = useState("");
  const [dialogCurrentStationErrors, setDialogCurrentStationErrors] = useState("");
  const [dialogMultiStationAdminStationList, setDialogMultiStationAdminStationList] = useState([]);
  const [dialogEditIsSetToDefault, setDialogEditIsSetToDefault] = useState(false);
  const [dialogIsInitialEmployeeAccessLevelLoad, setDialogIsInitialEmployeeAccessLevelLoad] = useState(false);
  const [dialogIsSaveTriggered, setDialogIsSaveTriggered] = useState(false);
  const [dialogIsSaveLoading, setDialogIsSaveLoading] = useState(false);
  const [dialogIsSaveError, setDialogIsSaveError] = useState(false);
  const [dialogSaveErrors, setDialogSaveErrors] = useState("");
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);

  const dialogIsEditRef = useRef(false);

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      const selectedStation = userSession.superuserStationList.includes(userSession.activeStation)
        ? userSession.activeStation
        : userSession.superuserStationList.slice().sort()[0];
      setSelectedSuperuserStation(selectedStation);
      setDialogMultiStationAdminStationList([selectedStation]);
      updateUserAccessOverrideList(selectedStation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState]);

  const updateUserAccessOverrideList = (station) => {
    setStationLatestUserAccessOverrideEvents([]);
    setIsLoading(true);
    return fetchLatestUserAccessOverrideEventsByStation(station, userSession.eid)
      .then((res) => res.json())
      .then((result) => {
        setStationLatestUserAccessOverrideEvents(
          convertUserAccessOverrideEventsToVisualizedTradeRequests(result.userAccessOverrideEvents)
        );
        setIsLoading(false);
        setIsFirstLoad(false);
      })
      .catch((err) => {
        console.error("error happened!", err);
        setIsLoading(false);
        setIsError(true);
      });
  };

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(userSession.isSuperuser);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  const convertUserAccessOverrideEventsToVisualizedTradeRequests = (userAccessOverrideEvents) => {
    let visualizedUserAccessOverrideEventsMap = {};
    let visualizedUserAccessOverrideEvents = [];
    for (let i = 0; i < userAccessOverrideEvents.length; i++) {
      let userAccessOverrideEvent = userAccessOverrideEvents[i];
      if (visualizedUserAccessOverrideEventsMap[userAccessOverrideEvent.eid]) {
        // map contains eid key already, erase value
        if (
          parseInt(visualizedUserAccessOverrideEventsMap[userAccessOverrideEvent.eid].createdAt) <
          parseInt(userAccessOverrideEvent.createdAt)
        ) {
          visualizedUserAccessOverrideEventsMap[userAccessOverrideEvent.eid] = userAccessOverrideEvent;
        }
      } else {
        visualizedUserAccessOverrideEventsMap[userAccessOverrideEvent.eid] = userAccessOverrideEvent;
      }
    }
    for (var key of Object.keys(visualizedUserAccessOverrideEventsMap)) {
      let userAccessOverrideEvent = visualizedUserAccessOverrideEventsMap[key];
      userAccessOverrideEvent.id = userAccessOverrideEvent.eid;
      userAccessOverrideEvent =
        convertUserAccessOverrideEventUTCIntegerFieldsToStationTimeZoneLuxonDateTime(userAccessOverrideEvent);
      if (userAccessOverrideEvent.eventType !== "DELETE") {
        visualizedUserAccessOverrideEvents.push(userAccessOverrideEvent);
      }
    }
    return visualizedUserAccessOverrideEvents;
  };

  const buildNewUserAccessOverrideEventPayload = () => {
    const payload = {
      action: "addUserAccessOverrideEvent",
      eventType: dialogIsEditRef.current
        ? dialogEditIsSetToDefault
          ? "DELETE"
          : "UPDATE"
        : dialogEditIsSetToDefault
        ? "DELETE"
        : "CREATE",
      eid: dialogEmployeeId,
      station: selectedSuperuserStation,
      firstName: dialogEmployeeFirstName,
      lastName: dialogEmployeeLastName,
      jobCode: dialogEmployeeJobCode,
      jobTitle: dialogEmployeeJobTitle,
      homeStation: dialogEmployeeHomeStation,
      isEmployee: isEmployee(),
      isAdmin: isAdmin(),
      isMultiStationAdmin: isMultiStationAdmin(),
      isSuperuser: false,
      multiStationAdminStationList: isMultiStationAdmin() ? dialogMultiStationAdminStationList : [],
      modifiedByEid: userSession.eid,
      modifiedByFirstName: userSession.firstName,
      modifiedByLastName: userSession.lastName,
    };

    if (dialogEmployeeTempStation) {
      payload.tempStation = dialogEmployeeTempStation;
      payload.isTemp = true;
    }

    return payload;
  };

  const isManageAccessDialogFormValidationSuccess = () => {
    if (dialogIsEditRef.current && dialogEditIsSetToDefault) {
      return true;
    }

    const stationErrors = validateStation(selectedSuperuserStation);

    let employeeIdErrors = validateEmployeeId(dialogEmployeeId);
    if (employeeIdErrors === "" && dialogEmployeeId !== dialogEmployeeData.eid) {
      // the eid was changed but we haven't pulled the fresh employee data yet (someone is doing something funky on the ui)
      fetchEmployeeData(dialogEmployeeId);
      return false;
    }

    const accessLevelErrors = validateAccessLevel(dialogSelectedAccessLevel);
    let adminStationsErrors = "";
    if (employeeIdErrors === "") {
      employeeIdErrors = validateEmployeeDataForManageAccess(dialogEmployeeData, selectedSuperuserStation);
    }
    if (isMultiStationAdmin()) {
      adminStationsErrors = validateSubmitAdminStationsForManageAccess(dialogMultiStationAdminStationList);
    }
    setDialogStationErrors(stationErrors);
    setDialogEmployeeIdErrors(employeeIdErrors);
    setDialogSelectedAccessLevelErrors(accessLevelErrors);
    setDialogCurrentStationErrors(adminStationsErrors);

    return stationErrors === "" && employeeIdErrors === "" && accessLevelErrors === "" && adminStationsErrors === "";
  };

  const submitAccessOverrideEvent = () => {
    if (!isManageAccessDialogFormValidationSuccess()) {
      return;
    }

    setDialogIsSaveTriggered(true);
    setDialogIsSaveLoading(true);

    const payload = buildNewUserAccessOverrideEventPayload();

    addUserAccessOverrideEvent(payload)
      .then((res) => res.json())
      .then((result) => {
        console.log(result);
        if (result.error) {
          console.error("Error");
          setDialogSaveErrors(result.error);
          setDialogIsSaveLoading(false);
          setDialogIsSaveError(true);
        } else if (result.newEvent) {
          console.log("Success!");
          updateUserAccessOverrideList(selectedSuperuserStation).then(() => {
            setDialogIsSaveLoading(false);
            handleClickDialogClose();
          });
        } else {
          console.error("Unknown response");
        }
      });
  };

  const handleClickDialogOpen = () => {
    setIsDialogOpen(true);
  };

  const handleClickDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsDialogOpen(false);
    clearDialogState();
  };

  const handleSelectedSuperuserStationChange = (event) => {
    setSelectedSuperuserStation(event.target.value);
    setDialogMultiStationAdminStationList([event.target.value]);
    updateUserAccessOverrideList(event.target.value);
  };

  // MANAGE ACCESS DIALOG METHODS - START

  const fetchEmployeeData = (eid) => {
    setDialogEditIsSetToDefault(false);
    setDialogEmployeeSearchLoaded(false);
    setDialogEmployeeSearchTriggered(true);
    setDialogEmployeeIdErrors("");
    setDialogEmployeeFirstName("");
    setDialogEmployeeLastName("");
    setDialogEmployeeJobCode("");
    setDialogEmployeeTempStation("");
    setDialogEmployeeHomeStation("");
    setDialogEmployeeActiveStation("");
    setDialogEmployeeJobTitle("");
    setDialogEmployeeWorkgroup("");
    fetchEmployee(eid)
      .then((res) => res.json())
      .then(async (result) => {
        if (!result.error && !result.message) {
          let employeeData = await getUserDataFromEmployeeService(result);
          let employeeDataErrors = validateEmployeeDataForManageAccess(employeeData, selectedSuperuserStation);
          if (employeeDataErrors !== "") {
            setDialogEmployeeIdErrors(employeeDataErrors);
            setDialogEmployeeSearchLoaded(true);
          }
          setDialogEmployeeData(employeeData);
          setDialogEmployeeFirstName(employeeData.firstName);
          setDialogEmployeeLastName(employeeData.lastName);
          setDialogEmployeeJobCode(employeeData.jobCode);
          if (employeeData.tempStation) {
            setDialogEmployeeTempStation(employeeData.tempStation);
          }
          setDialogEmployeeHomeStation(employeeData.homeStation);
          setDialogEmployeeActiveStation(employeeData.activeStation);
          setDialogEmployeeJobTitle(getJobTitleGivenJobCode(employeeData.jobCode));
          setDialogEmployeeWorkgroup(getWorkgroupGivenJobCode(employeeData.jobCode));
          setDialogEmployeeSearchLoaded(true);
          setDialogIsInitialEmployeeAccessLevelLoad(true);
          setCurrentAccessLevel(eid, employeeData.defaultAppRole);
        } else {
          setDialogEmployeeIdErrors("Invalid Employee ID");
          setDialogEmployeeSearchLoaded(true);
          setDialogEmployeeData(null);
          setDialogEmployeeFirstName("");
          setDialogEmployeeLastName("");
          setDialogEmployeeJobCode("");
          setDialogEmployeeTempStation("");
          setDialogEmployeeHomeStation("");
          setDialogEmployeeActiveStation("");
          setDialogEmployeeJobTitle("");
          setDialogEmployeeWorkgroup("");
          setDialogIsInitialEmployeeAccessLevelLoad(true);
        }
      });
  };

  const setCurrentAccessLevel = (eid, defaultAppRole) => {
    let currentAccessLevel = defaultAppRole;
    for (let i = 0; i < stationLatestUserAccessOverrideEvents.length; i++) {
      let currEvent = stationLatestUserAccessOverrideEvents[i];
      if (currEvent.eid === eid) {
        currentAccessLevel = getUserAccessLevelString(currEvent);
        if (currEvent.isMultiStationAdmin) {
          setDialogMultiStationAdminStationList(currEvent.multiStationAdminStationList);
        }
      }
    }
    setDialogEditIsSetToDefault(currentAccessLevel === defaultAppRole);
    setDialogSelectedAccessLevel(currentAccessLevel);
    return currentAccessLevel;
  };

  const clearDialogState = () => {
    setDialogEmployeeId("");
    dialogIsEditRef.current = false;
    setDialogIsInitialEmployeeAccessLevelLoad(false);
    setDialogEditIsSetToDefault(false);
    setDialogEmployeeSearchTriggered(false);
    setDialogEmployeeSearchLoaded(false);
    setDialogEmployeeIdErrors("");
    setDialogEmployeeData(null);
    setDialogEmployeeFirstName("");
    setDialogEmployeeLastName("");
    setDialogEmployeeJobCode("");
    setDialogEmployeeTempStation("");
    setDialogEmployeeHomeStation("");
    setDialogEmployeeActiveStation("");
    setDialogEmployeeJobTitle("");
    setDialogEmployeeWorkgroup("");
    setDialogSelectedAccessLevel("");
    setDialogCurrentStation("");
    setDialogCurrentStationErrors("");
    setDialogMultiStationAdminStationList([selectedSuperuserStation]);
    setDialogSelectedAccessLevelErrors("");
    setDialogIsSaveError(false);
    setDialogSaveErrors("");
    setDialogAccessOverrideEvent({});
  };

  const openEditManageAccessDialog = (row) => {
    dialogIsEditRef.current = true;
    setDialogAccessOverrideEvent(row);
    setDialogEmployeeId(row.eid);
    fetchEmployeeData(row.eid);
    setDialogSelectedAccessLevel(getUserAccessLevelString(row));
    if (row.isMultiStationAdmin) {
      setDialogMultiStationAdminStationList(row.multiStationAdminStationList);
    }
    handleClickDialogOpen();
  };

  const getUserAccessLevelString = (row) => {
    if (row.isEmployee) {
      return EMPLOYEE_ACCESS;
    } else if (row.isAdmin) {
      return ADMIN_ACCESS;
    } else if (row.isMultiStationAdmin) {
      return MULTI_STATION_ADMIN_ACCESS;
    }
    return "";
  };

  const isEmployee = () => {
    return dialogSelectedAccessLevel === EMPLOYEE_ACCESS;
  };

  const isAdmin = () => {
    return dialogSelectedAccessLevel === ADMIN_ACCESS;
  };

  const isMultiStationAdmin = () => {
    return dialogSelectedAccessLevel === MULTI_STATION_ADMIN_ACCESS;
  };

  const handleEmployeeIdChange = (event) => {
    setDialogEmployeeId(event.target.value.toLowerCase());
  };

  const handleEmployeeIdBlur = (event) => {
    let trimEmployeeId = event.target.value.trim();
    let errors = validateEmployeeId(event.target.value);
    if (errors !== "") {
      setDialogEmployeeIdErrors(errors);
    } else {
      if (!dialogEmployeeData || trimEmployeeId !== formatEmployeeIdFromKronos(dialogEmployeeData.kronosPersonNumber)) {
        setDialogSelectedAccessLevel("");
        setDialogMultiStationAdminStationList([selectedSuperuserStation]);
        fetchEmployeeData(event.target.value);
      }
    }
  };

  const handleEmployeeClear = () => {
    setDialogEmployeeId("");
    setDialogEmployeeData(null);
    setDialogEmployeeFirstName("");
    setDialogEmployeeLastName("");
    setDialogEmployeeJobCode("");
    setDialogEmployeeTempStation("");
    setDialogEmployeeHomeStation("");
    setDialogEmployeeActiveStation("");
    setDialogEmployeeJobTitle("");
    setDialogEmployeeWorkgroup("");
    setDialogSelectedAccessLevel("");
    setDialogMultiStationAdminStationList([selectedSuperuserStation]);
    setDialogEditIsSetToDefault(false);
    setDialogEmployeeSearchTriggered(false);
    setDialogEmployeeSearchLoaded(false);
    let errors = validateEmployeeId("");
    if (errors !== "") {
      setDialogEmployeeIdErrors(errors);
    }
  };

  const handleSelectedAccessLevelChange = (event) => {
    let accessLevelErrors = validateAccessLevel(event.target.value);

    let isSetToDefault = checkIfAccessIsSetToDefault(event.target.value);
    setDialogEditIsSetToDefault(isSetToDefault);

    setDialogIsInitialEmployeeAccessLevelLoad(false);
    setDialogSelectedAccessLevel(event.target.value);
    setDialogSelectedAccessLevelErrors(accessLevelErrors);
  };

  const checkIfAccessIsSetToDefault = (access) => {
    return dialogEmployeeData && dialogEmployeeData.defaultAppRole === access;
  };

  const handleCurrentStationChange = (event) => {
    setDialogCurrentStation(event.target.value.toUpperCase());
  };

  const handleAddStationClick = () => {
    let currentStationErrors = validateAddAdminStationForManageAccess(
      dialogCurrentStation,
      dialogMultiStationAdminStationList
    );

    setDialogCurrentStationErrors(currentStationErrors);
    if (currentStationErrors === "") {
      let newStationList = [...dialogMultiStationAdminStationList];

      newStationList.push(dialogCurrentStation);

      setDialogMultiStationAdminStationList(newStationList);
      setDialogCurrentStation("");
    }
  };

  const handleRemoveStationClick = (station) => {
    let newStationList = [...dialogMultiStationAdminStationList];
    var index = newStationList.indexOf(station);
    if (index > -1) {
      newStationList.splice(index, 1);
    }
    setDialogMultiStationAdminStationList(newStationList);
  };

  const handleSetToDefaultClick = () => {
    setDialogIsInitialEmployeeAccessLevelLoad(false);
    setDialogEditIsSetToDefault(true);
    setDialogSelectedAccessLevel(dialogEmployeeData?.defaultAppRole);
    setDialogEmployeeIdErrors("");
  };

  // MANAGE ACCESS DIALOG METHODS - END

  const renderStationMenuItems = () => {
    return userSession.superuserStationList
      .slice()
      .sort()
      .map((station) => {
        return (
          <MenuItem key={station} value={station}>
            {station}
          </MenuItem>
        );
      });
  };

  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }
  const breadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.ACCESS_EXCEPTIONS];
  const breadcrumbsLink = [HOME_PATH];
  const renderDesktopAccessExceptionsPage = () => {
    return (
      <>
        <StarsBreadcrumbs
          isConfirmationRequired={false}
          breadcrumbsLinks={breadcrumbsLink}
          breadcrumbsList={breadcrumbsList}
        />
        <div className="access-exceptions-title-row">
          <div className="page-title-left-alone">Access Exceptions</div>
          <FormControl variant="outlined" className="station-select-field">
            <InputLabel id="selectedSuperuserStationSelectLabel">Superuser Station</InputLabel>
            <Select
              labelId="selectedSuperuserStationSelectLabel"
              id="selectedSuperuserStationSelectElement"
              value={selectedSuperuserStation}
              onChange={handleSelectedSuperuserStationChange}
              label="Superuser Station"
            >
              <MenuItem value="" disabled>
                <em>Superuser Station</em>
              </MenuItem>
              {renderStationMenuItems()}
            </Select>
          </FormControl>
          <Button className="new-access-button" variant="contained" color="primary" onClick={handleClickDialogOpen}>
            <AddIcon className="button-icon" />
            <p className="new-access-button-text">ADD</p>
          </Button>
        </div>
        <div>
          <UserAccessOverrideEventTable
            isLoading={isLoading}
            isFirstLoad={isFirstLoad}
            loggedInUserData={userSession}
            eid={userSession.eid}
            isAdmin={userSession.isAdmin}
            events={stationLatestUserAccessOverrideEvents}
            isAdminPage={true}
            onClickDialogOpen={handleClickDialogOpen}
            onClickDialogClose={handleClickDialogClose}
            onOpenEditManageAccessDialog={openEditManageAccessDialog}
            isError={isError}
          />
        </div>
        <ManageAccessDialog
          selectedSuperuserStation={selectedSuperuserStation}
          stationErrors={dialogStationErrors}
          accessOverrideEvent={dialogAccessOverrideEvent}
          employeeId={dialogEmployeeId}
          employeeSearchTriggered={dialogEmployeeSearchTriggered}
          employeeSearchLoaded={dialogEmployeeSearchLoaded}
          employeeIdErrors={dialogEmployeeIdErrors}
          employeeFirstName={dialogEmployeeFirstName}
          employeeLastName={dialogEmployeeLastName}
          employeeJobCode={dialogEmployeeJobCode}
          employeeWorkgroup={dialogEmployeeWorkgroup}
          employeeTempStation={dialogEmployeeTempStation}
          employeeHomeStation={dialogEmployeeHomeStation}
          employeeActiveStation={dialogEmployeeActiveStation}
          selectedAccessLevel={dialogSelectedAccessLevel}
          selectedAccessLevelErrors={dialogSelectedAccessLevelErrors}
          currentStation={dialogCurrentStation}
          currentStationErrors={dialogCurrentStationErrors}
          multiStationAdminStationList={dialogMultiStationAdminStationList}
          isInitialEmployeeAccessLevelLoad={dialogIsInitialEmployeeAccessLevelLoad}
          isEdit={dialogIsEditRef.current}
          isEditSetToDefault={dialogEditIsSetToDefault}
          onSetToDefaultClick={handleSetToDefaultClick}
          isDialogOpen={isDialogOpen}
          onClickDialogOpen={handleClickDialogOpen}
          onClickDialogClose={handleClickDialogClose}
          onHandleEmployeeClear={handleEmployeeClear}
          onHandleEmployeeIdChange={handleEmployeeIdChange}
          onHandleEmployeeIdBlur={handleEmployeeIdBlur}
          onHandleSelectedAccessLevelChange={handleSelectedAccessLevelChange}
          onHandleCurrentStationChange={handleCurrentStationChange}
          onHandleAddStationClick={handleAddStationClick}
          onHandleRemoveStationClick={handleRemoveStationClick}
          onSubmitAccessOverrideEvent={submitAccessOverrideEvent}
          isSaveTriggered={dialogIsSaveTriggered}
          isSaveLoading={dialogIsSaveLoading}
          isSaveError={dialogIsSaveError}
          saveErrors={dialogSaveErrors}
          formValidationSuccess={isManageAccessDialogFormValidationSuccess}
          dialogEmployeeJobCode={dialogEmployeeJobCode}
          setDialogEditIsSetToDefault={setDialogEditIsSetToDefault}
        />
      </>
    );
  };

  const renderMobileAccessExceptionsPage = () => {
    return <div className="page-error">{ADMIN_MOBILE_WARNING_MESSAGE}</div>;
  };

  return (
    <div className="App-contents" data-testid="access-exceptions-page">
      {isUserAuthorizedStateLoading ? (
        <PageLoading />
      ) : (
        <>{props.isDesktopView ? renderDesktopAccessExceptionsPage() : renderMobileAccessExceptionsPage()}</>
      )}
    </div>
  );
};

export default AccessExceptionsPage;
